import React from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Images } from "../../../utils/Images";

// Styled components
const StyledCard = styled(Card)(({ theme, isMobile }) => ({
  width: isMobile ? "100%" : "100%",
  background: "#FFFFFF",
  boxShadow: "0px 10px 26px rgba(0, 0, 0, 0.04)",
  borderRadius: isMobile ? "10px" : "12px",
  overflow: "hidden",
  fontFamily: "'Red Hat Display', sans-serif",
  maxWidth: "100%",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  // boxShadow: "0px 0px 32px 0px #00000012",
  padding: "10px",
  //   '&:hover': {
  //     transform: 'translateY(-4px)',
  //     boxShadow: '0px 16px 32px rgba(0, 0, 0, 0.08)',
  //   },
}));

const ImageSection = styled(Box)(({ theme, isMobile }) => ({
  width: "100%",
  height: isMobile ? "180px" : "200px",
  position: "relative",
  borderRadius: isMobile ? "10px" : "12px",
  overflow: "hidden",
  marginBottom: isMobile ? "10px" : "12px",
  "@media (min-width: 1440px)": {
    height: "auto",
    aspectRatio: "16/9",
  },
}));

const BlogImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "12px",
  transition: "transform 0.3s ease",
  "@media (min-width: 1440px)": {
    height: "auto",
    objectFit: "cover",
    aspectRatio: "16/9",
  },
  //   '&:hover': {
  //     transform: 'scale(1.05)',
  //   },
});

const ContentSection = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  flexDirection: "column",
}));

const BlogTitle = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "700",
  fontSize: isMobile ? "16px" : "20px",
  lineHeight: isMobile ? "22px" : "28px",
  color: "#3E3232",
  marginBottom: isMobile ? "12px" : "16px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  minHeight: isMobile ? "44px" : "56px",
}));

const BlogDescription = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "400",
  fontSize: isMobile ? "12px" : "12px",
  lineHeight: isMobile ? "20px" : "24px",
  color: "#4A4A49",
  marginBottom: isMobile ? "16px" : "20px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  flex: 1,
}));

const FooterSection = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  paddingInline: isMobile ? "12px" : "16px",
  paddingBlock: isMobile ? "10px" : "18px",
  marginTop: "auto",
  backgroundColor: "#F5F5F5",
  borderRadius: isMobile ? "8px" : "12px",
}));

const DateContainer = styled(Box)(({ theme, isMobile }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
}));

const DateLabel = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "500",
  fontSize: isMobile ? "10px" : "12px",
  lineHeight: isMobile ? "14px" : "16px",
  color: "#686866",
  textTransform: "Capitalize",
  letterSpacing: "0.1px",
  marginBottom: "6px",
}));

const DateValue = styled(Typography)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "700",
  fontSize: isMobile ? "12px" : "14px",
  lineHeight: isMobile ? "16px" : "18px",
  color: "#1D1D1B",
}));

const ReadMoreButton = styled(Button)(({ theme, isMobile }) => ({
  // fontFamily: "Red Hat Display",
  fontWeight: "700",
  fontSize: isMobile ? "12px" : "14px",
  textTransform: "none",
  backgroundColor: "#1D1D1B",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: isMobile ? "8px 16px" : "13px 19px",
  minWidth: isMobile ? "80px" : "100px",
  height: isMobile ? "32px" : "40px",
  transition: "all 0.2s ease",
  boxShadow: "0px 4px 0px 0px #B9B9B8",
  "&:hover": {
    backgroundColor: "#1D1D1B",
    color: "#FFFFFF",
    // transform: 'translateY(-1px)',
    // boxShadow: "0px 4px 0px 0px #B9B9B8",
  },
  "&:active": {
    transform: "translateY(0px)",
  },
}));

// Helper function to format date
const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return new Date(dateString).toLocaleDateString("en-US", options);
};

const getImageSrc = (image) => {
  if (!image || (typeof image === "string" && !image.trim())) {
    return Images.fallBackImageCard;
  }
  if (typeof image === "string" && /^https?:\/\//.test(image)) {
    return image;
  }
  return image;
};

const BlogCard = ({ blogData }) => {
  const {
    id,
    image,
    title,
    description,
    date,
    buttonText = "Read More",
  } = blogData;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Handler for Read More button
  // Use Link for route navigation

  return (
    <StyledCard isMobile={isMobile}>
      {/* Image Section */}
      <ImageSection isMobile={isMobile}>
        <BlogImage
          src={getImageSrc(image)}
          alt={title}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = Images.fallBackImageCard;
          }}
        />
      </ImageSection>

      {/* Content Section */}
      <ContentSection isMobile={isMobile}>
        {/* Title */}
        <BlogTitle isMobile={isMobile}>{title}</BlogTitle>

        {/* Description */}
        <BlogDescription isMobile={isMobile}>{description}</BlogDescription>

        {/* Footer */}
        <FooterSection isMobile={isMobile}>
          <DateContainer isMobile={isMobile}>
            <DateLabel isMobile={isMobile}>Posted On</DateLabel>
            <DateValue isMobile={isMobile}>{formatDate(date)}</DateValue>
          </DateContainer>
          <ReadMoreButton
            isMobile={isMobile}
            aria-label={`Read more about ${title}`}
            component={Link}
            to={`/blog/${id}`}
          >
            Read More
          </ReadMoreButton>
        </FooterSection>
      </ContentSection>
    </StyledCard>
  );
};

export default BlogCard;